import type { WarmTransferActionsProps } from './index.types';
import { ReactComponent as HangUpIcon } from 'Assets/icons/hangup_v2.svg';
import { ReactComponent as TransferIcon } from 'Assets/icons/transfer-call_v2.svg';
import {
  TRANSFER_ACTION_NAMES,
  WARM_TRANSFER_COPY,
} from 'Modules/softphone/constants/callActionCopy';
import React from 'react';
import { SoftphoneActionButton } from '../SoftphoneActionButton';

const { transfer, end } = WARM_TRANSFER_COPY.consultation;

/** Names the group for assistive tech; not a call action, so it stays here. */
const TOOLBAR_ARIA_LABEL = 'Warm transfer actions';

/**
 * WarmTransferActions - Action buttons for completing or cancelling a warm transfer
 *
 * Renders two action buttons side by side:
 * - "Transfer" (completes the warm transfer): shown only once a person picks up,
 *   disabled with loading indicator when transfer completion is in progress
 * - "Hang up" (cancels the warm transfer): always enabled during warm transfer
 *
 * Both are {@link SoftphoneActionButton}s, so they advertise the same chords as
 * the actions they stand in for — Transfer keeps `T` (it completes the transfer
 * the consultation started) and Hang up keeps `E` with its usual caption. Only
 * the tooltips differ, naming what the buttons do to the *transfer*.
 *
 * @param {WarmTransferActionsProps} props - Component props
 * @returns {React.ReactElement} - Rendered WarmTransferActions component
 */
export const WarmTransferActions = ({
  onComplete,
  onCancel,
  isCompletingTransfer,
  isConsultationAnswered,
  testID,
}: WarmTransferActionsProps): React.ReactElement => {
  const componentTestId = testID ?  `${testID}-warm-transfer-actions`: "warm-transfer-actions";

  return (
    <div
      className="flex justify-center gap-[24px]"
      role="group"
      aria-label={TOOLBAR_ARIA_LABEL}
      data-testid={componentTestId}
    >
      {/* Complete warm transfer - hidden until consultation is connected */}
      {isConsultationAnswered && (
        <SoftphoneActionButton
          icon={TransferIcon}
          action="transfer"
          // Only the in-flight caption differs; "Transfer" comes from the table.
          label={isCompletingTransfer ? transfer.active.caption : undefined}
          ariaLabel={
            isCompletingTransfer ? undefined : TRANSFER_ACTION_NAMES.completeWarm
          }
          onClick={onComplete}
          variant="success"
          iconClassName="brightness-0 invert"
          disabled={isCompletingTransfer}
          copy={transfer}
          testID={`${componentTestId}-complete`}
        />
      )}

      {/* Cancel warm transfer */}
      <SoftphoneActionButton
        icon={HangUpIcon}
        action="end"
        onClick={onCancel}
        variant="destructive"
        iconClassName="brightness-0 invert"
        copy={end}
        testID={`${componentTestId}-cancel`}
      />
    </div>
  );
};
